import { Menu, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { finalPageData, siteNavigation } from '../../content/finalPageData';
import { analyticsGoals, trackAnalyticsGoal } from '../../lib/analytics/yandexMetrika';
import { BrandLogo } from '../brand-logo/BrandLogo';
import styles from './SiteHeader.module.scss';

export function SiteHeader() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(
    () => typeof window !== 'undefined' && window.scrollY > 24,
  );
  const headerRef = useRef<HTMLElement>(null);
  const firstMobileLinkRef = useRef<HTMLAnchorElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const closeOnDesktop = () => {
      if (window.matchMedia('(min-width: 1280px)').matches) setIsOpen(false);
    };

    window.addEventListener('resize', closeOnDesktop);
    return () => window.removeEventListener('resize', closeOnDesktop);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => firstMobileLinkRef.current?.focus(), 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        menuButtonRef.current?.focus();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = Array.from(
        headerRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((element) => element.getClientRects().length > 0);

      const firstElement = focusableElements[0];
      const lastElement = focusableElements.at(-1);

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <header
      ref={headerRef}
      className={`${styles.header} ${isScrolled ? styles.headerScrolled : ''} ${isOpen ? styles.headerMenuOpen : ''}`}
    >
      <a className={styles.brand} href="#top" aria-label="Лучезарная долина — на главную">
        <BrandLogo className={styles.brandLogo} compact={isScrolled && !isOpen} />
      </a>

      <nav className={styles.desktopNav} aria-label="Основная навигация">
        {siteNavigation.map((item) => (
          <a key={item.href} href={item.href}>
            {item.label}
          </a>
        ))}
      </nav>

      <a
        className={styles.desktopAction}
        href="#genplan"
        onClick={() => trackAnalyticsGoal(analyticsGoals.lotSelectClick)}
      >
        Выбрать участок
      </a>

      <button
        ref={menuButtonRef}
        className={styles.menuButton}
        type="button"
        aria-label={isOpen ? 'Закрыть меню' : 'Открыть меню'}
        aria-expanded={isOpen}
        aria-controls="mobile-navigation"
        onClick={() => setIsOpen((value) => !value)}
      >
        {isOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
      </button>

      <div
        id="mobile-navigation"
        className={`${styles.mobileNav} ${isOpen ? styles.mobileNavOpen : ''}`}
      >
        <div className={styles.mobileNavContent}>
          <nav aria-label="Мобильная навигация">
            {siteNavigation.map((item, index) => (
              <a
                ref={index === 0 ? firstMobileLinkRef : undefined}
                key={item.href}
                href={item.href}
                onClick={() => setIsOpen(false)}
              >
                <span>{String(index + 1).padStart(2, '0')}</span>
                {item.label}
              </a>
            ))}
          </nav>
          <a
            className={styles.mobileAction}
            href="#genplan"
            onClick={() => {
              setIsOpen(false);
              trackAnalyticsGoal(analyticsGoals.lotSelectClick);
            }}
          >
            Выбрать участок
          </a>
          {finalPageData.contacts.phone ? (
            <a className={styles.mobilePhone} href={finalPageData.contacts.phone.href}>
              {finalPageData.contacts.phone.label}
            </a>
          ) : null}
        </div>
      </div>
    </header>
  );
}
